import { inject, Injectable } from '@angular/core';
import {
  LoadVesselShiftsResult,
  VesselShift,
  VesselShiftResourceForm,
  VesselShiftResourceItem,
  VesselShiftResourceItemAuditLog,
  VesselShiftResourceItemSave,
  VesselShiftResourceType,
  VesselShiftTimeUpdate,
} from '@app/domain/entities/vessel-shift-resource.entity';
import { VesselShiftResourceRepository } from '@app/domain/repositories/vessel-shift-resource.repository';
import { PaginatedResponse } from '@app/shared/models/global.model';
import { map, Observable } from 'rxjs';
import { VesselShiftResourceService } from '../api-clients/vessel-shift-resource.service';
import { VesselShiftResourceMapper } from '../mappers/vessel-shift-resource.mapper';

@Injectable()
export class VesselShiftResourceRepositoryImpl implements VesselShiftResourceRepository {
  private readonly _mapper = new VesselShiftResourceMapper();
  private readonly _vesselShiftResourceService = inject(
    VesselShiftResourceService
  );

  getShifts(vesselByShiftId: number): Observable<VesselShift[]> {
    return this._vesselShiftResourceService
      .getShifts(vesselByShiftId)
      .pipe(
        map(items => (items ?? []).map(item => this._mapper.toShift(item)))
      );
  }

  loadShifts(vesselByShiftId: number): Observable<LoadVesselShiftsResult> {
    return this._vesselShiftResourceService
      .loadShifts(vesselByShiftId)
      .pipe(map(dto => this._mapper.toLoadResult(dto)));
  }

  updateShift(
    vesselByShiftId: number,
    shiftResourceId: number,
    body: VesselShiftTimeUpdate
  ): Observable<void> {
    return this._vesselShiftResourceService.updateShift(
      vesselByShiftId,
      shiftResourceId,
      body
    );
  }

  deleteShift(
    vesselByShiftId: number,
    shiftResourceId: number
  ): Observable<void> {
    return this._vesselShiftResourceService.deleteShift(
      vesselByShiftId,
      shiftResourceId
    );
  }

  getItems(
    vesselByShiftId: number,
    shiftResourceId: number,
    page: number,
    size: number
  ): Observable<PaginatedResponse<VesselShiftResourceItem>> {
    return this._vesselShiftResourceService
      .getItems(vesselByShiftId, shiftResourceId, page, size)
      .pipe(map(dto => this._mapper.toItemPage(dto)));
  }

  getResourceTypes(
    vesselByShiftId: number,
    shiftResourceId: number
  ): Observable<VesselShiftResourceType[]> {
    return this._vesselShiftResourceService
      .getResourceTypes(vesselByShiftId, shiftResourceId)
      .pipe(
        map(items =>
          (items ?? []).map(item => this._mapper.toResourceType(item))
        )
      );
  }

  getResourceForm(
    vesselByShiftId: number,
    shiftResourceId: number,
    configId: number
  ): Observable<VesselShiftResourceForm> {
    return this._vesselShiftResourceService
      .getResourceForm(vesselByShiftId, shiftResourceId, configId)
      .pipe(map(dto => this._mapper.toForm(dto)));
  }

  saveResourceItems(
    vesselByShiftId: number,
    shiftResourceId: number,
    configId: number,
    body: VesselShiftResourceItemSave[]
  ): Observable<void> {
    return this._vesselShiftResourceService.saveResourceItems(
      vesselByShiftId,
      shiftResourceId,
      configId,
      body
    );
  }

  deleteResourceItem(
    vesselByShiftId: number,
    shiftResourceId: number,
    configId: number,
    itemId: number
  ): Observable<void> {
    return this._vesselShiftResourceService.deleteResourceItem(
      vesselByShiftId,
      shiftResourceId,
      configId,
      itemId
    );
  }

  getItemAuditLogs(
    itemId: number
  ): Observable<VesselShiftResourceItemAuditLog[]> {
    return this._vesselShiftResourceService.getItemAuditLogs(itemId);
  }
}
